/**
 * Owned Media — listado de cuentas (§6.1 cards con filtros, §6.2 búsqueda
 * transversal / filtros / orden / agrupar / vistas guardadas, §6.4 estado vacío,
 * §13.1 paso 1: entra filtrado a "mis cuentas" con el buscador arriba).
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Bookmark, KeyRound, Lock, Plus, Search, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MultiSelectPopover } from '@/components/ui/multi-select-popover';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { EnumBadge } from '@/components/ui/enum-badge';
import { WinclapLoader } from '@/components/ui/winclap-loader';
import { trpc } from '@/lib/trpc';
import { getInitials } from '@/lib/avatar';
import { foldKey } from '../../../../shared/text-normalize';
import { OML_COMPLETENESS_BLOCKS, OML_LISTS, type OmlFichaStatus } from '../../../../shared/types/oml';
import { useOmlAccess, useOmlAccounts, useOmlCommunity, useOmlConfig, useOmlMutations, type OmlAccountCard } from '@/hooks/useOml';
import { EntityForm } from '@/components/owned-media/EntityForm';
import { ExportMenu } from '@/components/owned-media/ExportMenu';
import { AssetPreview } from '@/components/owned-media/AssetField';
import { FICHA_TONE, fichaLabel, statusTone } from '@/components/owned-media/oml-ui';

interface Filters {
  q: string; mine: boolean; country: string[]; platform: string[]; channel: string[]; brand: string[]; serviceType: string[]; status: string[];
  gp: string[]; person: string[]; journeyStatus: string[]; experimentStatus: string[]; projectStatus: string[]; ficha: string[];
  sort: 'name' | 'stale'; group: 'none' | 'country' | 'gp';
}
const EMPTY: Filters = { q: '', mine: true, country: [], platform: [], channel: [], brand: [], serviceType: [], status: [], gp: [], person: [], journeyStatus: [], experimentStatus: [], projectStatus: [], ficha: [], sort: 'name', group: 'none' };

export function OmlAccountsPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const { data: access } = useOmlAccess();
  const { data, isLoading } = useOmlAccounts(false);
  const { data: config } = useOmlConfig();
  const { data: members = [] } = useOmlCommunity();
  const views = trpc.oml.views.listViews.useQuery(undefined, { staleTime: 60_000 });
  const m = useOmlMutations();
  const [f, setF] = useState<Filters>({ ...EMPTY, ficha: params.get('ficha') ? [params.get('ficha')!] : [] });
  const [creating, setCreating] = useState(false);
  const [templateId, setTemplateId] = useState<string>('');
  const [saveViewOpen, setSaveViewOpen] = useState(false);
  const [viewName, setViewName] = useState('');
  const mineAvailable = !!access && Object.keys(access.accountRoles).length > 0;

  useEffect(() => { if (access && !mineAvailable) setF((p) => ({ ...p, mine: false })); }, [access, mineAvailable]);

  const names = useMemo(() => Object.fromEntries(members.map((x) => [x.email, x.name])), [members]);
  const accounts = useMemo(() => data?.accounts ?? [], [data]);
  const facet = (get: (a: OmlAccountCard) => string[]) => [...new Set(accounts.flatMap(get))].sort().map((v) => ({ value: v, label: v }));
  const options = {
    country: facet((a) => a.countries ?? []),
    platform: facet((a) => a.summary.platforms),
    channel: facet((a) => a.summary.channels as string[]),
    brand: facet((a) => a.summary.brands),
    serviceType: facet((a) => a.serviceTypes ?? []),
    status: OML_LISTS.accountStatus.values.map((v) => ({ value: v, label: v })),
    gp: facet((a) => a.summary.growthPartners).map((o) => ({ ...o, label: names[o.value] ?? o.value })),
    person: facet((a) => a.summary.teamEmails).map((o) => ({ ...o, label: names[o.value] ?? o.value })),
    journeyStatus: facet((a) => a.summary.journeyStatuses),
    experimentStatus: facet((a) => a.summary.experimentStatuses),
    projectStatus: facet((a) => a.summary.projectStatuses),
    ficha: (['al_dia', 'desactualizada', 'no_aplica'] as OmlFichaStatus[]).map((v) => ({ value: v, label: fichaLabel(t, v) })),
  };

  const filtered = useMemo(() => {
    const q = foldKey(f.q);
    const inc = (sel: string[], vals: string[]) => !sel.length || sel.some((s) => vals.includes(s));
    const rows = accounts.filter((a) =>
      (!f.mine || a.access.isTeam || (access?.accountRoles[a.id]?.length ?? 0) > 0)
      && (!q || a.summary.searchIndex.includes(q))
      && inc(f.country, a.countries ?? []) && inc(f.platform, a.summary.platforms) && inc(f.channel, a.summary.channels as string[]) && inc(f.brand, a.summary.brands)
      && inc(f.serviceType, a.serviceTypes ?? []) && inc(f.status, [a.status]) && inc(f.gp, a.summary.growthPartners) && inc(f.person, a.summary.teamEmails)
      && inc(f.journeyStatus, a.summary.journeyStatuses) && inc(f.experimentStatus, a.summary.experimentStatuses) && inc(f.projectStatus, a.summary.projectStatuses)
      && inc(f.ficha, [a.summary.fichaStatus]));
    rows.sort((x, y) => (f.sort === 'stale' ? String(x.lastReviewedAt).localeCompare(String(y.lastReviewedAt)) : x.name.localeCompare(y.name)));
    return rows;
  }, [accounts, f, access]);

  const groups = useMemo(() => {
    if (f.group === 'none') return [{ key: '', rows: filtered }];
    const map = new Map<string, OmlAccountCard[]>();
    for (const a of filtered) {
      const keys = f.group === 'country' ? (a.countries?.length ? a.countries : ['—']) : (a.summary.growthPartners.length ? a.summary.growthPartners.map((e) => names[e] ?? e) : ['—']);
      for (const k of keys) map.set(k, [...(map.get(k) ?? []), a]);
    }
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([key, rows]) => ({ key, rows }));
  }, [filtered, f.group, names]);

  const applyView = (filters: Record<string, unknown>) => setF({ ...EMPTY, ...(filters as Partial<Filters>) });
  // "Crear" de la matriz 9.2 acota a Lead/admin (16/10/2026); el fallback es para un probe viejo sin el flag.
  const canCreate = !!access && (access.canCreateAccount ?? (access.isLead || access.isAdmin));
  const exportIds = filtered.filter((a) => a.access.canExport).map((a) => a.id);

  if (isLoading || !access) return <div className="flex justify-center py-20"><WinclapLoader size={48} className="text-foreground/25" /></div>;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">{t('oml.nav.title', 'Owned Media')}</h1>
          <p className="text-sm text-muted-foreground">{t('oml.accounts.subtitle', 'Cómo funciona cada cuenta: quién es quién, con qué plataformas se opera, qué journeys y campañas corren y con qué reglas.')} <EnumBadge value="N3" tone="warning" label={t('oml.n3', 'Confidencial de negocio · N3')} /></p>
        </div>
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild><Button variant="outline" size="sm"><Bookmark className="h-4 w-4 mr-1" />{t('oml.views.button', 'Mis vistas')}</Button></DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {(views.data ?? []).map((v) => (
                <DropdownMenuItem key={v.id} onClick={() => applyView(v.filters)} className="flex justify-between gap-3">
                  <span>{v.name}</span>
                  <button type="button" className="text-muted-foreground hover:text-destructive" disabled={m.deleteView.isPending} onClick={(e) => { e.stopPropagation(); m.deleteView.mutate({ id: v.id }, { onSuccess: () => views.refetch() }); }} aria-label={t('common.remove', 'Quitar')}><Trash2 className="h-3.5 w-3.5" /></button>
                </DropdownMenuItem>
              ))}
              {(views.data ?? []).length > 0 && <DropdownMenuSeparator />}
              <DropdownMenuItem onClick={() => setSaveViewOpen(true)}>{t('oml.views.save', 'Guardar la vista actual…')}</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <ExportMenu canExport={exportIds.length > 0} scope="accounts" accountIds={exportIds} filters={f as unknown as Record<string, unknown>} />
          {canCreate && <Button size="sm" onClick={() => setCreating(true)}><Plus className="h-4 w-4 mr-1" />{t('oml.accounts.new', 'Nueva cuenta')}</Button>}
        </div>
      </div>

      <div className="rounded-lg border bg-card p-3 space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input className="pl-8" value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} placeholder={t('oml.accounts.search_ph', 'Buscar cuenta, cliente, marca, plataforma, stakeholder, journey, campaña, experimento, proyecto o segmento')} />
          </div>
          {mineAvailable && <label className="flex items-center gap-2 text-sm"><Switch checked={f.mine} onCheckedChange={(c) => setF({ ...f, mine: c })} />{t('oml.accounts.mine', 'Mis cuentas')}</label>}
          <Select value={f.sort} onValueChange={(v) => setF({ ...f, sort: v as Filters['sort'] })}>
            <SelectTrigger className="w-[210px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="name">{t('oml.accounts.sort_name', 'Orden: alfabético')}</SelectItem>
              <SelectItem value="stale">{t('oml.accounts.sort_stale', 'Orden: más desactualizadas primero')}</SelectItem>
            </SelectContent>
          </Select>
          <Select value={f.group} onValueChange={(v) => setF({ ...f, group: v as Filters['group'] })}>
            <SelectTrigger className="w-[190px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">{t('oml.accounts.group_none', 'Sin agrupar')}</SelectItem>
              <SelectItem value="country">{t('oml.accounts.group_country', 'Agrupar por país')}</SelectItem>
              <SelectItem value="gp">{t('oml.accounts.group_gp', 'Agrupar por Growth Partner')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-wrap gap-2">
          <MultiSelectPopover label={t('oml.filters.country', 'País')} options={options.country} selected={f.country} onChange={(v) => setF({ ...f, country: v })} allLabel={t('common.all', 'Todos')} />
          <MultiSelectPopover label={t('oml.filters.platform', 'Plataforma')} options={options.platform} selected={f.platform} onChange={(v) => setF({ ...f, platform: v })} allLabel={t('common.all', 'Todos')} />
          <MultiSelectPopover label={t('oml.filters.channel', 'Canal')} options={options.channel} selected={f.channel} onChange={(v) => setF({ ...f, channel: v })} allLabel={t('common.all', 'Todos')} />
          <MultiSelectPopover label={t('oml.filters.brand', 'Marca')} options={options.brand} selected={f.brand} onChange={(v) => setF({ ...f, brand: v })} allLabel={t('common.all', 'Todos')} />
          <MultiSelectPopover label={t('oml.filters.service', 'Tipo de servicio')} options={options.serviceType} selected={f.serviceType} onChange={(v) => setF({ ...f, serviceType: v })} allLabel={t('common.all', 'Todos')} />
          <MultiSelectPopover label={t('oml.filters.status', 'Estado de cuenta')} options={options.status} selected={f.status} onChange={(v) => setF({ ...f, status: v })} allLabel={t('common.all', 'Todos')} />
          <MultiSelectPopover label={t('oml.filters.gp', 'Growth Partner')} options={options.gp} selected={f.gp} onChange={(v) => setF({ ...f, gp: v })} allLabel={t('common.all', 'Todos')} />
          <MultiSelectPopover label={t('oml.filters.person', 'Persona asignada')} options={options.person} selected={f.person} onChange={(v) => setF({ ...f, person: v })} allLabel={t('common.all', 'Todos')} />
          <MultiSelectPopover label={t('oml.filters.journey', 'Estado de journey')} options={options.journeyStatus} selected={f.journeyStatus} onChange={(v) => setF({ ...f, journeyStatus: v })} allLabel={t('common.all', 'Todos')} />
          <MultiSelectPopover label={t('oml.filters.experiment', 'Estado de experimento')} options={options.experimentStatus} selected={f.experimentStatus} onChange={(v) => setF({ ...f, experimentStatus: v })} allLabel={t('common.all', 'Todos')} />
          <MultiSelectPopover label={t('oml.filters.project', 'Estado de proyecto')} options={options.projectStatus} selected={f.projectStatus} onChange={(v) => setF({ ...f, projectStatus: v })} allLabel={t('common.all', 'Todos')} />
          <MultiSelectPopover label={t('oml.filters.ficha', 'Estado de la ficha')} options={options.ficha} selected={f.ficha} onChange={(v) => setF({ ...f, ficha: v })} allLabel={t('common.all', 'Todos')} />
          <Button variant="ghost" size="sm" onClick={() => setF({ ...EMPTY, mine: mineAvailable })}>{t('common.clear_filters', 'Limpiar')}</Button>
        </div>
      </div>

      {accounts.length === 0 ? (
        <EmptyState canCreate={canCreate} onCreate={() => setCreating(true)} />
      ) : filtered.length === 0 ? (
        <p className="text-sm text-muted-foreground py-10 text-center">{t('oml.accounts.no_match', 'Ninguna cuenta coincide con los filtros.')}</p>
      ) : (
        groups.map((g) => (
          <section key={g.key} className="space-y-3">
            {g.key && <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">{g.key} · {g.rows.length}</h2>}
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
              {g.rows.map((a) => <AccountCard key={a.id} a={a} names={names} />)}
            </div>
          </section>
        ))
      )}

      {creating && (
        <Dialog open onOpenChange={(o) => { if (!o) setCreating(false); }}>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle>{t('oml.accounts.new', 'Nueva cuenta')}</DialogTitle></DialogHeader>
            <p className="text-sm text-muted-foreground">{t('oml.accounts.template_hint', 'Arrancá de una plantilla por tipo de servicio (precarga procesos y reglas de canal) o de cero.')}</p>
            <Select value={templateId || '__none__'} onValueChange={(v) => setTemplateId(v === '__none__' ? '' : v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">{t('oml.accounts.no_template', 'Sin plantilla')}</SelectItem>
                {(config?.settings.templates ?? []).map((tpl) => <SelectItem key={tpl.id} value={tpl.id}>{tpl.name} · {tpl.serviceType}</SelectItem>)}
              </SelectContent>
            </Select>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreating(false)}>{t('common.cancel', 'Cancelar')}</Button>
              <NewAccountButton templateId={templateId || undefined} config={config} members={members} onDone={() => setCreating(false)} />
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      <Dialog open={saveViewOpen} onOpenChange={setSaveViewOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>{t('oml.views.save_title', 'Guardar vista')}</DialogTitle></DialogHeader>
          <Input value={viewName} onChange={(e) => setViewName(e.target.value)} placeholder={t('oml.views.name_ph', 'Nombre, por ejemplo "Fichas desactualizadas"')} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveViewOpen(false)}>{t('common.cancel', 'Cancelar')}</Button>
            <Button disabled={!viewName.trim() || m.saveView.isPending} onClick={() => m.saveView.mutate({ name: viewName.trim(), filters: f as unknown as Record<string, unknown> }, { onSuccess: () => { setSaveViewOpen(false); setViewName(''); views.refetch(); } })}>{t('common.save', 'Guardar')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function NewAccountButton({ templateId, config, members, onDone }: { templateId?: string; config: ReturnType<typeof useOmlConfig>['data']; members: { email: string; name: string }[]; onDone: () => void }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button onClick={() => setOpen(true)}>{t('common.continue', 'Continuar')}</Button>
      {open && <EntityForm open onOpenChange={(o) => { setOpen(o); if (!o) onDone(); }} type="account" accountId="new" config={config} members={members} templateId={templateId} />}
    </>
  );
}

function AccountCard({ a, names }: { a: OmlAccountCard; names: Record<string, string> }) {
  const { t } = useTranslation();
  const s = a.summary;
  return (
    <Link to={`/owned-media/accounts/${a.id}`} className="block group">
      <Card className="h-full transition-shadow group-hover:shadow-md">
        <CardContent className="p-4 space-y-3">
          <div className="flex items-start gap-3">
            <div className="h-11 w-11 rounded-md border bg-muted flex items-center justify-center overflow-hidden shrink-0 text-sm font-semibold">
              {a.logoAssetId ? <AssetPreview accountId={a.id} assetId={String(a.logoAssetId)} compact /> : getInitials(a.name)}
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-semibold truncate">{a.name}</div>
              <div className="text-xs text-muted-foreground truncate">{a.client} · {(a.countries ?? []).join(', ')}</div>
            </div>
            <div className="flex flex-col items-end gap-1">
              <EnumBadge value={a.status} tone={statusTone(a.status)} />
              <EnumBadge value={s.fichaStatus} tone={FICHA_TONE[s.fichaStatus]} label={fichaLabel(t, s.fichaStatus)} />
            </div>
          </div>
          <div className="flex flex-wrap gap-1">
            {(a.serviceTypes ?? []).map((x) => <EnumBadge key={x} value={x} tone="accent" />)}
            {s.platforms.map((p) => <EnumBadge key={p} value={p} tone="info" />)}
            {(s.channels as string[]).map((c) => <EnumBadge key={c} value={c} tone="neutral" />)}
          </div>
          <div className="text-xs text-muted-foreground flex flex-wrap gap-x-3 gap-y-1">
            <span>GP: {s.growthPartners.length ? s.growthPartners.map((e) => names[e] ?? e).join(', ') : '—'}</span>
            <span>{t('oml.accounts.active_journeys', '{{n}} journeys activos', { n: s.activeJourneys })}</span>
            <span>{t('oml.accounts.team', '{{n}} en el equipo', { n: s.teamCount })}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="h-1.5 flex-1 rounded bg-muted overflow-hidden"><div className="h-full bg-emerald-500" style={{ width: `${s.completeness.percent}%` }} /></div>
            <span className="text-xs text-muted-foreground">{s.completeness.percent} %</span>
            {s.riskyAccesses > 0 && <AlertTriangle className="h-4 w-4 text-rose-600" aria-label={t('oml.platform.risky', 'Acceso por cuenta compartida o personal')} />}
            {s.expiringAccesses > 0 && <KeyRound className="h-4 w-4 text-amber-600" aria-label={t('oml.platform.expiring', 'Vence en menos de 30 días')} />}
            {!a.access.canSeeCommercial && <Lock className="h-4 w-4 text-amber-700" aria-label={t('oml.locked.hint', 'Campo restringido: existe, pero no lo ves por permisos.')} />}
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

function EmptyState({ canCreate, onCreate }: { canCreate: boolean; onCreate: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="rounded-lg border border-dashed p-10 text-center space-y-4">
      <h2 className="text-lg font-semibold">{t('oml.empty.title', 'Todavía no hay cuentas cargadas')}</h2>
      <p className="text-sm text-muted-foreground max-w-xl mx-auto">{t('oml.empty.desc', 'Una cuenta es usable cuando tiene sus siete bloques mínimos:')}</p>
      <ol className="text-sm text-left max-w-md mx-auto list-decimal pl-6 space-y-1">
        {OML_COMPLETENESS_BLOCKS.map((b) => <li key={b}>{t(`oml.completeness.${b}`, b)}</li>)}
      </ol>
      {canCreate && <Button onClick={onCreate}><Plus className="h-4 w-4 mr-1" />{t('oml.empty.create', 'Crear la primera cuenta')}</Button>}
    </div>
  );
}
